/**
 * Connexions et actions d'un technicien sur les postes, une journée (demande William 09/10/2026).
 *
 * Le technicien qui remplit sa saisie voit OÙ il s'est connecté, à quelle heure, chez quelle
 * société — « là je me suis connecté chez tel client, je n'ai pas fait de ticket ». Une société
 * où il s'est connecté sans aucune tâche à son nom ce jour-là est signalée.
 *
 * Source : GET /api/reports/technician-time/devices. Le serveur ramène un non-superadmin à
 * lui-même et retire les postes réservés au superadmin (COMPTÉS : `masques_verrou`) ; ce
 * composant ne décide d'aucun droit. La route est ouverte pendant le verrou de saisie de 13 h.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Monitor } from 'lucide-react';
import { apiFetch } from '../lib/api';
import DataList from './DataList';

// Mêmes libellés que la page Temps techniciens : « Prise de main » atteste une demande
// (mot de passe affiché), « Connexion établie » une connexion vue par le serveur.
export const LIBELLE_EVENEMENT = {
  remote_takeover: 'Prise de main',
  remote_intent: 'Lancement outil',
  remote_session: 'Connexion établie',
  transfer: 'Transfert de poste',
  profile_delete: 'Suppression profil',
  adhoc_task: 'Script',
};

const mono = { fontFamily: "'IBM Plex Mono', monospace" };

function heure(at, avecDate = false) {
  if (!at) return '—';
  const d = new Date(at);
  if (Number.isNaN(d.getTime())) return '—';
  const h = d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  return avecDate ? `${d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })} ${h}` : h;
}

function jourLocal(at) {
  const d = at ? new Date(at) : null;
  return d && !Number.isNaN(d.getTime()) ? d.toDateString() : '';
}

/**
 * Tableau seul (sans chargement) : réutilisable là où les événements sont déjà lus.
 * `societesAvecTache` : Set des client_id où une tâche a été saisie ce jour-là ; absent, pas de repère.
 */
export function TableConnexionsPostes({ events, societesAvecTache = null, tableId = 'connexions-postes',
  emptyMessage = 'Aucune trace de connexion sur cette période.' }) {
  const lignes = useMemo(
    () => (events || []).map((e, i) => ({ ...e, _cle: `${e.at || ''}|${e.type}|${e.device || ''}|${i}` })),
    [events],
  );
  // Sur plusieurs jours (page Temps techniciens, période), l'heure seule serait ambiguë.
  const avecDate = new Set((events || []).map((e) => jourLocal(e.at)).filter(Boolean)).size > 1;
  const columns = [
    { key: 'at', label: 'Heure', sortable: true, width: avecDate ? 110 : 70,
      sortValue: (e) => (e.at ? Date.parse(e.at) : null),
      render: (e) => <span style={mono}>{heure(e.at, avecDate)}</span> },
    { key: 'type', label: 'Quoi', sortable: true, width: 140,
      sortValue: (e) => LIBELLE_EVENEMENT[e.type] || e.type,
      render: (e) => (
        <span style={{ fontSize: 11, color: '#075985', background: '#e0f2fe', borderRadius: 3, padding: '1px 6px' }}>
          {LIBELLE_EVENEMENT[e.type] || e.type}
        </span>
      ) },
    { key: 'device', label: 'Poste', sortable: true, width: 160,
      render: (e) => <strong style={{ color: '#1e293b' }}>{e.device || '—'}</strong> },
    { key: 'client_name', label: 'Société', sortable: true,
      render: (e) => (e.client_name
        ? e.client_name
        : <span style={{ color: '#94a3b8', fontStyle: 'italic' }}>{e.societe_incertaine ? 'plusieurs postes portent ce nom' : 'inconnue'}</span>) },
    { key: 'duree', label: 'Durée', sortable: true, width: 90,
      sortValue: (e) => (typeof e.params?.duree_secondes === 'number' ? e.params.duree_secondes : null),
      render: (e) => <span style={{ ...mono, color: '#475569' }}>{e.type === 'remote_session' ? (e.result || '—') : '—'}</span> },
  ];
  if (societesAvecTache) {
    columns.push({
      key: 'repere', label: 'Ticket ?', sortable: true, width: 190,
      sortValue: (e) => (e.client_id && !societesAvecTache.has(e.client_id) ? 0 : 1),
      render: (e) => {
        if (!e.client_id) return <span style={{ color: '#94a3b8' }}>—</span>;
        if (societesAvecTache.has(e.client_id)) return <span style={{ color: '#059669' }}>✓ tâche saisie</span>;
        return (
          <span style={{ color: '#b45309', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <AlertTriangle size={12} /> aucune tâche ce jour
          </span>
        );
      },
    });
  }
  return (
    <DataList
      data={lignes}
      columns={columns}
      rowKeyField="_cle"
      tableId={tableId}
      defaultSort={{ key: 'at', direction: 'asc' }}
      sansPagination
      emptyMessage={emptyMessage}
    />
  );
}

/**
 * Bloc « Mes connexions aux postes » d'une journée, pour la saisie.
 * `taches` : tickets tracés du jour (chacun porte `client_id`) — sert au repère « aucune tâche ».
 */
export default function ConnexionsPostesDuJour({ date, userEmail, taches }) {
  const [etat, setEtat] = useState({ loading: true });

  useEffect(() => {
    if (!date) return undefined;
    let annule = false;
    setEtat({ loading: true });
    const params = new URLSearchParams({
      period: 'custom', from: `${date}T00:00:00`, to: `${date}T23:59:59`,
    });
    // Pour un non-superadmin, le serveur ignore ce paramètre et le ramène à lui-même.
    if (userEmail) params.set('technician_id', userEmail);
    apiFetch(`/api/reports/technician-time/devices?${params}`)
      .then((r) => r.json())
      .then((d) => { if (!annule) setEtat(d && d.ok ? { ...d, loading: false } : { loading: false, erreur: true }); })
      .catch(() => { if (!annule) setEtat({ loading: false, erreur: true }); });
    return () => { annule = true; };
  }, [date, userEmail]);

  const societesAvecTache = useMemo(
    () => new Set((taches || []).map((t) => t.client_id).filter(Boolean)),
    [taches],
  );
  const events = etat.events || [];
  const sansTache = new Set(events.filter((e) => e.client_id && !societesAvecTache.has(e.client_id)).map((e) => e.client_name || e.client_id));

  return (
    <div>
      {etat.loading && <div style={{ fontSize: 12, color: '#94a3b8' }}>Chargement…</div>}
      {etat.erreur && <div style={{ fontSize: 12, color: '#b91c1c' }}>Chargement des connexions impossible.</div>}
      {!etat.loading && !etat.erreur && (
        <>
          {sansTache.size > 0 && (
            <div role="status" style={{ display: 'flex', gap: 6, alignItems: 'flex-start', fontSize: 12, color: '#92400e', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6, padding: '6px 10px', marginBottom: 8 }}>
              <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: 1 }} />
              <span>
                Connexion sans aucune tâche à ton nom ce jour-là chez : <strong>{[...sansTache].join(', ')}</strong>.
                {' '}Si tu y as travaillé, crée ou complète le ticket.
              </span>
            </div>
          )}
          <TableConnexionsPostes events={events} societesAvecTache={societesAvecTache} tableId="saisie-connexions-postes"
            emptyMessage="Aucune trace de connexion ce jour-là." />
          {etat.masques_verrou > 0 && (
            <div style={{ marginTop: 6, fontSize: 11, color: '#7a5c00' }}>
              {etat.masques_verrou} trace{etat.masques_verrou > 1 ? 's' : ''} masquée{etat.masques_verrou > 1 ? 's' : ''} : poste réservé aux superadmins ou non identifiable.
            </div>
          )}
          {(etat.limites || []).length > 0 && (
            <details style={{ marginTop: 6, fontSize: 11, color: '#64748b' }}>
              <summary style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <Monitor size={11} /> Ce que cette liste ne voit pas
              </summary>
              <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
                {etat.limites.map((l) => <li key={l}>{l}</li>)}
              </ul>
            </details>
          )}
        </>
      )}
    </div>
  );
}
